import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Wallet as WalletIcon, LogIn, User as UserIcon, Clock, Bell } from 'lucide-react';
import authService from '../../services/authService';
import { useRecommendations } from '../../hooks/useRecommendations';

export const Navbar = ({ onConnect }) => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [user, setUser] = React.useState(null);
  const { recommendations, count } = useRecommendations();
  const [showNotifications, setShowNotifications] = React.useState(false);

  React.useEffect(() => {
    const unsubscribe = authService.subscribeToAuthChanges((u) => {
      setUser(u);
    });
    return () => unsubscribe();
  }, []);

  const handleGoogleLogin = async () => {
    try {
      await authService.signInWithGoogle();
    } catch (error) {
      console.error("Login failed:", error);
    }
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 border-b border-white/5 bg-[#0a0a0b]/80 backdrop-blur-xl">
      <div className="container mx-auto flex h-16 md:h-16 items-center justify-between px-4 gap-2">
        {/* Left: Logo */}
        <div className="flex items-center gap-3 md:gap-10 flex-shrink-0">
          {/* Logo and Name */}
          <Link to="/" className="flex items-center gap-2 md:gap-4 hover:opacity-80 transition-opacity">
            <img src="/claw_logo.png" alt="PredictClaw Logo" className="h-8 w-8 md:h-10 md:w-10 rounded-full object-cover border border-white/20 shadow-[0_0_10px_rgba(255,77,77,0.3)]" />
            <span className="text-lg md:text-2xl font-display font-black tracking-tighter text-white uppercase">PredictClaw</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <NavLink to="/markets" active={location.pathname === '/markets'}>Markets</NavLink>
            <NavLink to="/portfolio" active={location.pathname === '/portfolio'}>Portfolio</NavLink>
            <NavLink to="/leaderboard" active={location.pathname === '/leaderboard'}>Leaderboard</NavLink>
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
          {/* Google Auth Section */}
          <div className="flex items-center gap-2 mr-2 border-r border-white/10 pr-4">

            {/* Notifications Bell */}
            <div className="relative mr-2">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 rounded-lg hover:bg-white/10 transition-colors"
                title="Trade Recommendations"
              >
                <Bell className="w-5 h-5 text-muted-foreground hover:text-white transition-colors" />
                {count > 0 && (
                  <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full border border-black animate-pulse"></span>
                )}
              </button>

              {/* Notifications Dropdown */}
              {showNotifications && (
                <div className="absolute right-0 mt-2 w-80 bg-[#121212] border border-white/10 rounded-xl shadow-2xl p-4 z-50 animate-in fade-in zoom-in duration-200">
                  <div className="flex items-center justify-between mb-3 border-b border-white/10 pb-2">
                    <h3 className="text-sm font-bold text-white">Recommendations</h3>
                    <span className="text-xs text-muted-foreground bg-white/5 px-2 py-0.5 rounded-full">{recommendations.length} Active</span>
                  </div>

                  {recommendations.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-4 text-center">
                      No active recommendations.
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[300px] overflow-y-auto custom-scrollbar pr-1">
                      {recommendations.map((rec) => (
                        <div key={rec.id} className="text-xs p-3 rounded-lg bg-black/40 border border-white/5">
                          <div className="font-bold text-white mb-1 truncate">{rec.marketTitle}</div>
                          <div className={`text-[10px] mb-1 ${rec.type === 'SUCCESS' ? 'text-emerald-400' :
                            rec.type === 'WARNING' ? 'text-yellow-400' :
                              rec.type === 'DANGER' ? 'text-red-400' : 'text-blue-400'
                            }`}>
                            {rec.type === 'SUCCESS' ? '✅ ALIGNED' : rec.type === 'WARNING' ? '⚠️ HEDGE SUGGESTED' : '🚨 CRITICAL'}
                          </div>
                          <div className="text-muted-foreground">{rec.message}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {user ? (
              <div className="flex items-center gap-3">
                <div className="hidden md:flex flex-col items-end">
                  <span className="text-[10px] font-bold text-white uppercase tracking-wider truncate max-w-[100px]">
                    {user.displayName || 'User'}
                  </span>
                  <button
                    onClick={handleLogout}
                    className="text-[8px] font-bold text-muted-foreground hover:text-primary uppercase tracking-widest transition-colors"
                  >
                    Logout
                  </button>
                </div>
                {user.photoURL ? (
                  <img src={user.photoURL} alt="Profile" className="w-8 h-8 rounded-full border border-primary/50" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center border border-primary/50 text-primary">
                    <UserIcon className="w-4 h-4" />
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={handleGoogleLogin}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-colors group"
              >
                <LogIn className="w-4 h-4 text-primary group-hover:scale-110 transition-transform" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-white hidden sm:inline">Google Login</span>
              </button>
            )}
          </div>

          {/* Wallet Section - Coming Soon */}
          <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 cursor-not-allowed opacity-70">
            <WalletIcon className="w-3.5 h-3.5 text-muted-foreground" />
            <div className="flex items-center gap-1.5">
              <Clock className="w-3 h-3 text-yellow-500" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                Wallet Coming Soon
              </span>
            </div>
          </div>
          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-white hover:bg-white/10 rounded-lg transition-colors flex-shrink-0"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {isMobileMenuOpen && (
        <div className="md:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl absolute left-0 right-0 p-4 flex flex-col gap-4 animate-in slide-in-from-top-2">
          <NavLink to="/markets" active={location.pathname === '/markets'} onClick={() => setIsMobileMenuOpen(false)}>Markets</NavLink>
          <NavLink to="/portfolio" active={location.pathname === '/portfolio'} onClick={() => setIsMobileMenuOpen(false)}>Portfolio</NavLink>
          <NavLink to="/leaderboard" active={location.pathname === '/leaderboard'} onClick={() => setIsMobileMenuOpen(false)}>Leaderboard</NavLink>
        </div>
      )}
    </nav>
  );
};

const NavLink = ({ to, children, active }) => {
  return (
    <Link
      to={to}
      className={`text-sm font-medium transition-colors hover:text-primary ${active ? 'text-primary' : 'text-muted-foreground'
        }`}
    >
      {children}
    </Link>
  );
};
